import React, { useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import CloseButton from "../CloseButton/CloseButton";
import { createWorkspace } from "../../api/workspaces";
import type { WorkspaceScopeType } from "../../api/workspaces";
import ScopeSelector from "./ScopeSelector";
import styles from "./CreateWorkspaceModal.module.css";

const CONCERN_CATEGORIES = [
  "environmental",
  "infrastructure",
  "health",
  "education",
  "economic",
  "social",
  "governance",
  "other",
] as const;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Community GUID — optional for personal/household scope. */
  communityGuid?: string;
  /** Current user GUID — becomes team lead on the new workspace. */
  currentUserGuid: string;
  /** Called after successful creation with the new workspace GUID. */
  onCreated: (workspaceGuid: string) => void;
  /** If true, skip the scope selector and default to the given scope. */
  defaultScope?: WorkspaceScopeType;
}

const CreateWorkspaceModal: React.FC<Props> = ({
  isOpen,
  onClose,
  communityGuid,
  currentUserGuid,
  onCreated,
  defaultScope,
}) => {
  const { t } = useAppTranslation("pages");

  /* ── Step management ── */
  const [step, setStep] = useState<"scope" | "details">(
    defaultScope ? "details" : "scope"
  );

  /* ── Form state ── */
  const [scopeType, setScopeType] = useState<WorkspaceScopeType | null>(
    defaultScope ?? null
  );
  const [concern, setConcern] = useState("");
  const [category, setCategory] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setConcern("");
      setCategory("");
      setError(null);
      setSubmitting(false);
      setScopeType(defaultScope ?? null);
      setStep(defaultScope ? "details" : "scope");
    }
  }, [isOpen, defaultScope]);

  // Escape to close
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const handleScopeNext = useCallback(() => {
    if (scopeType) setStep("details");
  }, [scopeType]);

  const handleSubmit = useCallback(async () => {
    if (!concern.trim() || !scopeType) return;
    setSubmitting(true);
    setError(null);
    try {
      const ws = await createWorkspace({
        communityGuid: communityGuid || undefined,
        presentingConcern: concern.trim(),
        teamLeadGuid: currentUserGuid,
        scopeType,
        concernCategory: category || undefined,
      });
      onCreated(ws.workspaceGuid);
      onClose();
    } catch (e: any) {
      setError(e?.message ?? t("workspace.createError"));
    } finally {
      setSubmitting(false);
    }
  }, [
    concern,
    category,
    scopeType,
    communityGuid,
    currentUserGuid,
    onCreated,
    onClose,
    t,
  ]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className={styles.backdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.modal} role="dialog" aria-modal="true">
        <div className={styles.header}>
          <span className={styles.headerTitle}>
            {t("workspace.createTitle")}
          </span>
          <CloseButton onClick={onClose} size="md" />
        </div>

        <div className={styles.body}>
          {error && <div className={styles.errorMsg}>{error}</div>}

          {/* ── Step 1: Scope selection ── */}
          {step === "scope" && (
            <ScopeSelector value={scopeType} onChange={setScopeType} />
          )}

          {/* ── Step 2: Problem details ── */}
          {step === "details" && (
            <>
              {/* Scope indicator */}
              {scopeType && (
                <div className={styles.scopeIndicator}>
                  <span className={styles.scopeLabel}>
                    {t("workspace.scope.selectedLabel")}
                  </span>
                  <span className={styles.scopeValue}>
                    {t(`workspace.scope.${scopeType}Title`)}
                  </span>
                  {!defaultScope && (
                    <button
                      type="button"
                      className={styles.changeLink}
                      onClick={() => setStep("scope")}
                    >
                      {t("workspace.scope.change")}
                    </button>
                  )}
                </div>
              )}

              <div className={styles.field}>
                <label className={styles.label}>
                  {scopeType === "community"
                    ? t("workspace.presentingConcern")
                    : t("workspace.scope.personalConcernLabel")}
                </label>
                <textarea
                  className={styles.textarea}
                  value={concern}
                  onChange={(e) => setConcern(e.target.value)}
                  placeholder={
                    scopeType === "community"
                      ? t("workspace.presentingConcernPlaceholder")
                      : t("workspace.scope.personalConcernPlaceholder")
                  }
                  autoFocus
                  maxLength={2000}
                />
                <span className={styles.hint}>
                  {scopeType === "community"
                    ? t("workspace.presentingConcernHint")
                    : t("workspace.scope.personalConcernHint")}
                </span>
              </div>

              <div className={styles.field}>
                <label className={styles.label}>
                  {t("workspace.concernCategory")}
                </label>
                <select
                  className={styles.select}
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="">
                    {t("workspace.concernCategoryPlaceholder")}
                  </option>
                  {CONCERN_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {t(`workspace.categoryLabels.${cat}`)}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>

        <div className={styles.footer}>
          {step === "scope" ? (
            <>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={onClose}
              >
                {t("workspace.cancel")}
              </button>
              <button
                type="button"
                className={styles.submitBtn}
                onClick={handleScopeNext}
                disabled={!scopeType}
              >
                {t("workspace.scope.next")}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={onClose}
                disabled={submitting}
              >
                {t("workspace.cancel")}
              </button>
              <button
                type="button"
                className={styles.submitBtn}
                onClick={handleSubmit}
                disabled={submitting || !concern.trim()}
              >
                {submitting ? t("workspace.creating") : t("workspace.create")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CreateWorkspaceModal;
